/**
 * services/queue/queueMappers.ts
 * Translate between the wire QueueDto (sk-queue-api field names) and the local
 * cfg1/cfg2 view shapes the Operator (Tier-2) and Config (Tier-1) screens use.
 * Kept in one place so both screens map identically.
 */

import type { QueueDto, QueuePatchTier1, QueuePatchTier2 } from '../../api/queueDtos';
import type { Cfg1, Cfg2, Channel } from '../../queue/types';

const ALL_CHANNELS: Channel[] = ['QR', 'VOICE', 'STAFF', 'WEB', 'WHATSAPP'];

/** Wire queue → Tier-2 operator view (cfg2). */
export function dtoToCfg2(q: QueueDto): Cfg2 {
  const ch = new Set(q.channelsEnabled);
  return {
    self_join: q.selfJoinEnabled,
    shared_seating: q.sharedSeatingEnabled,
    accepting: q.acceptingEntries,
    self_removal: q.selfRemovalEnabled,
    ch_QR: ch.has('QR'),
    ch_VOICE: ch.has('VOICE'),
    ch_STAFF: ch.has('STAFF'),
    ch_WEB: ch.has('WEB'),
    ch_WHATSAPP: ch.has('WHATSAPP'),
    deposit: q.depositRequired,
    depositAmt: q.depositAmount != null ? Math.round(Number(q.depositAmount)) : 0,
    no_show: q.noShowChargeEnabled,
    noShowAmt: q.noShowChargeAmount != null ? Math.round(Number(q.noShowChargeAmount)) : 0,
    maxParty: q.maxSelfJoinParty ?? 12,
    // Lifecycle windows (seconds → minutes for the UI) — now Tier-2.
    holdMin: Math.round(q.holdSeconds / 60),
    graceMin: Math.round(q.graceSeconds / 60),
    maxWaitMin: q.maxWaitSeconds != null ? Math.round(q.maxWaitSeconds / 60) : 0,
  };
}

/** Wire queue → Tier-1 config view (cfg1). */
export function dtoToCfg1(q: QueueDto): Cfg1 {
  return {
    matchType: q.matchType,
    maxParallel: q.maxParallel ?? 1,
    ackGraceSec: q.ackGraceS ?? 60,
    agingCap: q.agingCap ?? 0,
    allowOversize: q.allowOversizing ?? false,
    oversizeThreshold: q.oversizeThreshold ?? 0,
  };
}

/** Full cfg2 → Tier-2 PATCH body (the channel set is rebuilt from the toggles). */
export function cfg2ToPatch(c: Cfg2): QueuePatchTier2 {
  const channelsEnabled = ALL_CHANNELS.filter((ch) => {
    switch (ch) {
      case 'QR': return c.ch_QR;
      case 'VOICE': return c.ch_VOICE;
      case 'STAFF': return c.ch_STAFF;
      case 'WEB': return c.ch_WEB;
      case 'WHATSAPP': return c.ch_WHATSAPP;
    }
  });
  return {
    selfJoinEnabled: c.self_join,
    sharedSeatingEnabled: c.shared_seating,
    acceptingEntries: c.accepting,
    selfRemovalEnabled: c.self_removal,
    channelsEnabled: channelsEnabled.length ? channelsEnabled : undefined, // never PATCH an empty set
    depositRequired: c.deposit,
    depositAmount: c.depositAmt,
    noShowChargeEnabled: c.no_show,
    noShowChargeAmount: c.noShowAmt,
    maxSelfJoinParty: c.maxParty,
    // Lifecycle windows (minutes → seconds for the wire) — now Tier-2.
    holdSeconds: c.holdMin * 60,
    graceSeconds: c.graceMin * 60,
    maxWaitSeconds: c.maxWaitMin > 0 ? c.maxWaitMin * 60 : null,
  };
}

/** Full cfg1 → Tier-1 PATCH body (rev-5 Setup fields). */
export function cfg1ToPatch(c: Cfg1): QueuePatchTier1 {
  return {
    matchType: c.matchType,
    maxParallel: c.maxParallel,
    ackGraceS: c.ackGraceSec,
    agingCap: c.agingCap,
    allowOversizing: c.allowOversize,
    // Guard only bites with a threshold set; send it so it actually persists.
    oversizeThreshold: c.oversizeThreshold > 0 ? c.oversizeThreshold : null,
  };
}
